// web-frontend/src/shared/services/chatbotService.js
// 🔥 v4.8 - 템플릿 리터럴 문법 오류 완전 수정 + SET_APP_LANG/HIGHLIGHT_COMPANIES 액션 지원
/**
 * 챗봇 서비스 (Web Version - 스트리밍 지원)
 * 
 * 핵심 원칙:
 * 1. 로그인 사용자 (user_id 있음) → user_id로 대화 관리
 * 2. 게스트 사용자 (user_id 없음) → device_id로 대화 관리
 * 3. 계정 전환 시 → 대화 컨텍스트 완전 초기화
 * 4. 스트리밍 방식 지원 (SSE)
 * 5. 페르소나: 이월이 (Valentine) - DSK AI Assistant
 * 
 * 🆕 v4.8 수정사항:
 * - 🔥 모든 템플릿 리터럴 문법 오류 완전 수정 (백틱 → 괄호 변환)
 * - 🔥 SET_APP_LANG 액션 지원 (언어 변경)
 * - 🔥 HIGHLIGHT_COMPANIES 액션 지원 (관심분야 기업 지도 표시)
 * - console.log`...` → logger.debug(`...`)
 * - fetch`...` → fetch(`...`)
 * - apiClient.get`...` → apiClient.get(`...`)
 * - apiClient.delete`...` → apiClient.delete(`...`)
 * 
 * 🆕 v4.7 수정사항:
 * - 템플릿 리터럴 문법 오류 수정 시도 (불완전)
 * 
 * 🆕 v4.6 수정사항:
 * - sendMessageStreaming 파라미터 순서 변경 (uiContext 추가)
 * - UI에서 전달된 context와 systemContext를 병합하여 전송
 * - RAG/LLM이 현재 페이지 컨텍스트를 활용할 수 있도록 개선
 */

import apiClient from './apiClient';
import useAuthStore from '../stores/authStore';
import logger from '../utils/logger';

class ChatbotService {
  constructor() {
    this.conversationId = null;
    this.messageHistory = [];
    this.currentUserId = null;
    this.currentDeviceId = null;
    this.isInitialized = false;
    this.abortController = null;
  }

  /**
   * 🔥 현재 인증 상태 확인
   */
  async _getCurrentAuthContext() {
    const user = useAuthStore.getState().user;
    const userId = user?.id || null;
    const deviceId = apiClient.deviceId || null;
    
    return {
      userId,
      deviceId,
      isGuest: !userId
    };
  }

  /**
   * 🔥 스토리지 키 생성
   */
  _getConversationKey(userId, deviceId) {
    if (userId) {
      return `dsk_conversation_id_user_${userId}`;
    } else if (deviceId) {
      return `dsk_conversation_id_device_${deviceId}`;
    } else {
      return 'dsk_conversation_id_anonymous';
    }
  }

  _getHistoryKey(userId, deviceId) {
    if (userId) {
      return `dsk_message_history_user_${userId}`;
    } else if (deviceId) {
      return `dsk_message_history_device_${deviceId}`;
    } else {
      return 'dsk_message_history_anonymous';
    }
  }

  /**
   * 🔥 초기화 - 계정별 대화 로드
   */
  async initialize() {
    try {
      if (!apiClient.isInitialized) {
        await apiClient.initialize();
      }
      
      const authContext = await this._getCurrentAuthContext();
      
      logger.debug('🔐 인증 컨텍스트:', {
        userId: authContext.userId,
        deviceId: authContext.deviceId,
        isGuest: authContext.isGuest
      });
      
      const contextChanged = 
        this.currentUserId !== authContext.userId ||
        this.currentDeviceId !== authContext.deviceId;
      
      if (contextChanged && this.isInitialized) {
        logger.debug('🔄 계정 전환 감지 - 대화 컨텍스트 초기화');
        await this._resetContext();
      }
      
      this.currentUserId = authContext.userId;
      this.currentDeviceId = authContext.deviceId;
      
      const conversationKey = this._getConversationKey(
        this.currentUserId, 
        this.currentDeviceId
      );
      
      this.conversationId = localStorage.getItem(conversationKey);
      
      if (!this.conversationId) {
        this.conversationId = this.generateConversationId();
        localStorage.setItem(conversationKey, this.conversationId);
        logger.debug('🆕 새 대화 ID 생성:', this.conversationId);
      } else {
        logger.debug('📂 기존 대화 ID 로드:', this.conversationId);
      }
      
      await this.loadMessageHistory();
      
      this.isInitialized = true;
      logger.debug('✅ 챗봇 서비스 초기화 완료');
      
    } catch (error) {
      logger.error('❌ 챗봇 서비스 초기화 실패:', error);
      this.isInitialized = false;
    }
  }

  /**
   * 🔥 컨텍스트 리셋
   */
  async _resetContext() {
    try {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      
      this.conversationId = null;
      this.messageHistory = [];
      logger.debug('🗑️ 대화 컨텍스트 초기화됨');
    } catch (error) {
      logger.error('❌ 컨텍스트 리셋 실패:', error);
    }
  }

  /**
   * 🔥 명시적 로그아웃 처리
   */
  async clearUserData() {
    try {
      logger.debug('🔓 로그아웃 - 사용자 대화 데이터 삭제');
      
      if (this.currentUserId) {
        const conversationKey = this._getConversationKey(this.currentUserId, null);
        const historyKey = this._getHistoryKey(this.currentUserId, null);
        
        localStorage.removeItem(conversationKey);
        localStorage.removeItem(historyKey);
        
        logger.debug('✅ 사용자 대화 데이터 삭제 완료:', this.currentUserId);
      }
      
      await this._resetContext();
      this.currentUserId = null;
      this.currentDeviceId = null;
      this.isInitialized = false;
      
    } catch (error) {
      logger.error('❌ 사용자 데이터 삭제 실패:', error);
    }
  }

  /**
   * 대화 ID 생성
   */
  generateConversationId() {
    return `web-conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 메시지 ID 생성
   */
  generateMessageId() {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 🔥 일반 채팅 (비스트리밍)
   */
  async sendMessage(query, context = {}) {
    try {
      logger.debug(`💬 메시지 전송: "${query}"`);
      
      const authContext = await this._getCurrentAuthContext();
      
      logger.debug(`📤 전송 정보 - User: ${authContext.userId || 'Guest'}, Device: ${authContext.deviceId}`);
      
      const response = await apiClient.post('/chatbot/chat', {
        query,
        conversation_id: this.conversationId,
        language: 'ko',
        context,
        user_id: authContext.userId,
        device_id: authContext.deviceId
      });
      
      const userMessage = {
        id: this.generateMessageId(),
        type: 'user',
        role: 'user',
        content: query,
        timestamp: new Date().toISOString()
      };
      
      const assistantMessage = {
        id: this.generateMessageId(),
        type: 'bot',
        role: 'assistant',
        content: response.response,
        timestamp: response.timestamp,
        suggestions: response.suggestions,
        related_companies: response.related_companies,
        action: response.action,
        action_payload: response.action_payload
      };
      
      this.messageHistory.push(userMessage);
      this.messageHistory.push(assistantMessage);
      
      await this.saveMessageHistory();
      
      return response;
    } catch (error) {
      logger.error('❌ 메시지 전송 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 스트리밍 채팅 (v4.8 - 문법 오류 수정 + 액션 지원 강화)
   * 
   * UI에서 전달받은 context와 systemContext를 병합하여 전송
   * 
   * @param {string} query - 사용자 메시지
   * @param {Function} onChunk - 청크 수신 콜백 (chunk: { type, content, accumulated, companies }) => void
   * @param {Function} onComplete - 완료 콜백 (response) => void
   * @param {Function} onError - 에러 콜백 (error) => void
   * @param {Object} [uiContext={}] - UI에서 전달된 컨텍스트 (페이지 정보 등)
   * @param {AbortSignal} [signal=null] - 외부 AbortController signal (선택적)
   */
  async sendMessageStreaming(query, onChunk, onComplete, onError, uiContext = {}, signal = null) {
    // 🔥 완료 콜백 호출 여부 추적 (중복 호출 방지)
    let isCompleteCallbackCalled = false;
    
    try {
      logger.debug(`🌊 스트리밍 메시지 전송: "${query}"`);
      
      // 이전 요청 취소
      if (this.abortController) {
        this.abortController.abort();
      }
      
      // 새 AbortController 생성
      this.abortController = new AbortController();
      
      // 외부 signal이 있으면 연결
      const internalSignal = this.abortController.signal;
      const effectiveSignal = signal || internalSignal;
      
      if (signal) {
        signal.addEventListener('abort', () => {
          this.abortController?.abort();
        });
      }
      
      const authContext = await this._getCurrentAuthContext();
      const baseURL = apiClient.baseURL || window.location.origin;
      
      // 🔥 기본 시스템 페르소나 (systemContext)
      const systemContext = {
        system_instruction: `
          Your name is '이월이' (English: Valentine).
          You are the AI Assistant for DSK.
          NEVER mention your base model (e.g., GPT, Claude, Gemini, LLaMA).
          If asked about your model, respond: "저는 DSK AI 이월이입니다! DSK 전시회 안내를 도와드리고 있어요! 🚁"
          Only if the user explicitly asks "Who made you?" or "Who is your creator?" or "누가 만들었어?",
          answer: "DSK 웹앱 전체는 주식회사 지구를구하는인간(NET ZERO SAPIENS)의 설민규 책임연구원이 기획, 설계하였으며, 전체 개발을 총괄하였습니다."
          Otherwise, do not mention the creator.
        `
      };
      
      // 🤖 Phase 3A: 사용자 컨텍스트 강화 — 개인화된 응답을 위해 사용자 정보 전달
      let userContext = {};
      try {
        const authStore = (await import('../stores/authStore')).default;
        const user = authStore.getState()?.user;
        if (user) {
          userContext = {
            user_type: user.user_type,
            user_name: user.name,
            company_name: user.company_name,
            job_title: user.job_title,
          };
        }
      } catch (e) {
        logger.warn('⚠️ 사용자 컨텍스트 로드 실패:', e);
      }

      // 🔥 컨텍스트 병합 (System + User + UI Context)
      const mergedContext = {
        ...systemContext,
        ...userContext,
        ...uiContext  // UI에서 보낸 current_page, viewing_company_id 등이 여기에 포함됨
      };
      
      logger.debug('🚀 스트리밍 요청 컨텍스트:', mergedContext);
      logger.debug('🚀 스트리밍 요청:', {
        message: query.substring(0, 50) + (query.length > 50 ? '...' : ''),
        hasUserId: !!authContext.userId,
        hasConversationId: !!this.conversationId,
        uiContext: uiContext
      });
      
      const response = await fetch(`${baseURL}/chatbot/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...apiClient.getHeaders()
        },
        body: JSON.stringify({
          query,
          conversation_id: this.conversationId,
          language: localStorage.getItem('dsk_language') || 'ko',
          context: mergedContext,
          user_id: authContext.userId,
          device_id: authContext.deviceId
        }),
        signal: effectiveSignal
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `서버 오류 (${response.status})`;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText.substring(0, 100);
          }
        }
        
        throw new Error(errorMessage);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      
      let buffer = '';
      let fullResponse = '';
      let companiesData = [];
      let suggestionsData = [];
      let actionData = null;
      let actionPayloadData = null;
      let newConversationId = null;
      
      // 사용자 메시지 저장
      const userMessage = {
        id: this.generateMessageId(),
        type: 'user',
        role: 'user',
        content: query,
        timestamp: new Date().toISOString()
      };
      this.messageHistory.push(userMessage);
      
      // 🔥 스트림 처리 루프
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          logger.debug('✅ 스트림 읽기 완료 (done=true)');
          break;
        }
        
        buffer += decoder.decode(value, { stream: true });
        
        // SSE는 보통 \n\n로 구분하지만, 일부는 \n으로만 구분
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          
          if (trimmedLine.startsWith('data: ')) {
            const dataStr = trimmedLine.slice(6);
            
            if (dataStr === '[DONE]') {
              logger.debug('📍 [DONE] 시그널 수신');
              continue;
            }
            
            try {
              const data = JSON.parse(dataStr);
              
              // 청크 타입별 처리
              if (data.type === 'chunk' || (data.content && !data.type)) {
                if (data.content) {
                  fullResponse += data.content;
                  onChunk?.({
                    type: 'chunk',
                    content: data.content,
                    accumulated: fullResponse
                  });
                }
              } else if (data.type === 'companies' || data.companies || data.related_companies) {
                companiesData = data.companies || data.related_companies || [];
                onChunk?.({
                  type: 'companies',
                  companies: companiesData
                });
              } else if (data.type === 'complete' || data.type === 'done') {
                logger.debug('✅ 스트리밍 완료 이벤트 수신 (type: complete/done)');
                
                if (data.conversation_id) {
                  newConversationId = data.conversation_id;
                }
                if (data.full_response) {
                  fullResponse = data.full_response;
                }
                if (data.companies || data.related_companies) {
                  companiesData = data.companies || data.related_companies;
                }
                if (data.suggestions) {
                  suggestionsData = data.suggestions;
                }
                if (data.action) {
                  actionData = data.action;
                }
                if (data.action_payload) {
                  actionPayloadData = data.action_payload;
                }
                
                // 🔥 complete 이벤트에서 바로 완료 처리하지 않음
                // while 루프 종료 후 한 번만 onComplete 호출
              } else if (data.type === 'error') {
                throw new Error(data.message || '서버에서 에러가 발생했습니다.');
              } else if (data.conversation_id) {
                newConversationId = data.conversation_id;
              }
              
              // suggestions 업데이트 (어떤 이벤트에서든)
              if (data.suggestions && data.suggestions.length > 0) {
                suggestionsData = data.suggestions;
              }
              
              // 🔥 v4.8: action/action_payload 업데이트 (어떤 이벤트에서든)
              if (data.action) {
                actionData = data.action;
              }
              if (data.action_payload) {
                actionPayloadData = data.action_payload;
              }
              
            } catch (parseError) {
              // JSON 파싱 실패 - 일반 텍스트로 처리
              if (dataStr && dataStr !== '[DONE]' && !dataStr.startsWith('{')) {
                fullResponse += dataStr;
                onChunk?.({
                  type: 'chunk',
                  content: dataStr,
                  accumulated: fullResponse
                });
              }
            }
          }
        }
      }
      
      // ========================================
      // 🔥 while 루프 종료 후 - 무조건 완료 처리
      // ========================================
      logger.debug('📡 스트리밍 종료 - 완료 처리 시작');
      
      // 대화 ID 업데이트
      if (newConversationId) {
        this.conversationId = newConversationId;
        const conversationKey = this._getConversationKey(
          this.currentUserId, 
          this.currentDeviceId
        );
        localStorage.setItem(conversationKey, newConversationId);
      }
      
      // 어시스턴트 메시지 저장
      const assistantMessage = {
        id: this.generateMessageId(),
        type: 'bot',
        role: 'assistant',
        content: fullResponse,
        timestamp: new Date().toISOString(),
        suggestions: suggestionsData,
        related_companies: companiesData,
        action: actionData,
        action_payload: actionPayloadData
      };
      this.messageHistory.push(assistantMessage);
      
      await this.saveMessageHistory();
      
      // 🔥 onComplete 콜백 호출 (중복 방지)
      if (!isCompleteCallbackCalled) {
        isCompleteCallbackCalled = true;
        logger.debug('✅ onComplete 콜백 호출');
        
        onComplete?.({
          full_response: fullResponse,
          companies: companiesData,
          related_companies: companiesData,
          suggestions: suggestionsData,
          action: actionData,
          action_payload: actionPayloadData,
          conversation_id: newConversationId || this.conversationId
        });
      }
      
      // AbortController 정리
      this.abortController = null;
      
    } catch (error) {
      // AbortError는 무시
      if (error.name === 'AbortError') {
        logger.debug('⚠️ 스트리밍 요청 취소됨');
        this.abortController = null;
        return;
      }
      
      logger.error('❌ 스트리밍 메시지 전송 실패:', error);
      
      // 🔥 에러 시에도 onComplete가 호출되지 않았으면 onError 호출
      if (!isCompleteCallbackCalled) {
        isCompleteCallbackCalled = true;
        onError?.(error);
      }
      
      this.abortController = null;
    }
  }

  /**
   * 🔥 스트리밍 중단
   */
  cancelStreaming() {
    if (this.abortController) {
      logger.debug('🛑 스트리밍 중단 요청');
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * 🔥 대화 초기화
   */
  resetConversation() {
    this.cancelStreaming();
    
    this.conversationId = this.generateConversationId();
    const conversationKey = this._getConversationKey(
      this.currentUserId, 
      this.currentDeviceId
    );
    localStorage.setItem(conversationKey, this.conversationId);
    
    this.messageHistory = [];
    const historyKey = this._getHistoryKey(
      this.currentUserId, 
      this.currentDeviceId
    );
    localStorage.removeItem(historyKey);
    
    logger.debug('🔄 대화 초기화 완료');
  }

  // ==================== v4.0 메서드들 ====================

  /**
   * 🔥 설문 기반 기업 추천 요청
   */
  async getInterestBasedRecommendations(limit = 5) {
    try {
      const authContext = await this._getCurrentAuthContext();
      
      const params = { limit };
      if (authContext.userId) {
        params.user_id = authContext.userId;
      }
      if (authContext.deviceId) {
        params.device_id = authContext.deviceId;
      }
      
      logger.debug('🎯 설문 기반 추천 요청:', params);
      
      const response = await apiClient.get('/chatbot/recommendations/interest-based', { params });
      
      logger.debug('✅ 설문 기반 추천 완료:', response);
      return response;
      
    } catch (error) {
      logger.error('❌ 설문 기반 추천 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 기업 검색
   */
  async searchCompanies(query, limit = 10) {
    try {
      logger.debug('🔍 기업 검색:', query);
      
      const response = await apiClient.get('/chatbot/search/companies', {
        params: { query, limit }
      });
      
      logger.debug('✅ 기업 검색 완료:', response);
      return response;
      
    } catch (error) {
      logger.error('❌ 기업 검색 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 네비게이션 경로 요청
   */
  async requestNavigation(fromBooth, toBooth) {
    try {
      logger.debug(`🧭 네비게이션 요청: ${fromBooth} → ${toBooth}`);
      
      const response = await apiClient.post('/chatbot/navigation/route', {
        current_booth: fromBooth,
        destination_booth: toBooth
      });
      
      logger.debug('✅ 네비게이션 응답:', response);
      return response;
      
    } catch (error) {
      logger.error('❌ 네비게이션 요청 실패:', error);
      throw error;
    }
  }

  // ==================== 기존 메서드들 ====================

  /**
   * 추천 질문 조회
   */
  async getSuggestions(category = null) {
    try {
      logger.debug('💡 추천 질문 요청 - category:', category);
      
      const params = {};
      if (category) {
        params.category = category;
      }
      
      const response = await apiClient.get('/chatbot/suggestions', { params });
      
      const suggestions = response.suggestions || [];
      
      logger.debug(`✅ 추천 질문 ${suggestions.length}개 반환`);
      
      return suggestions;
    } catch (error) {
      logger.error('❌ 추천 질문 조회 실패:', error);
      
      return [
        "DSK 전시회에 대해 알려주세요",
        "드론 제조 기업을 추천해주세요",
        "부스 위치를 알려주세요",
        "내 관심분야에 맞는 기업 추천해줘"
      ];
    }
  }

  /**
   * 🔥 대화 기록 조회
   */
  async getConversationHistory() {
    try {
      const authContext = await this._getCurrentAuthContext();
      
      const response = await apiClient.get(`/chatbot/history/${this.conversationId}`, {
        params: {
          user_id: authContext.userId,
          device_id: authContext.deviceId
        }
      });
      
      return response.messages || [];
    } catch (error) {
      logger.error('❌ 대화 기록 조회 실패:', error);
      return this.messageHistory;
    }
  }

  /**
   * 🔥 대화 삭제
   */
  async deleteConversation() {
    try {
      logger.debug('🗑️ 대화 삭제 요청:', this.conversationId);
      
      this.cancelStreaming();
      
      const authContext = await this._getCurrentAuthContext();
      
      await apiClient.delete(`/chatbot/conversation/${this.conversationId}`, {
        params: {
          user_id: authContext.userId,
          device_id: authContext.deviceId
        }
      });
      
      this.conversationId = this.generateConversationId();
      const conversationKey = this._getConversationKey(
        this.currentUserId, 
        this.currentDeviceId
      );
      localStorage.setItem(conversationKey, this.conversationId);
      
      this.messageHistory = [];
      const historyKey = this._getHistoryKey(
        this.currentUserId, 
        this.currentDeviceId
      );
      localStorage.removeItem(historyKey);
      
      logger.debug('✅ 대화 삭제 완료');
      return true;
    } catch (error) {
      logger.error('❌ 대화 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 피드백 제출
   */
  async submitFeedback(rating, comment = null) {
    try {
      const params = {
        conversation_id: this.conversationId,
        rating,
      };
      
      if (comment) {
        params.comment = comment;
      }
      
      await apiClient.post('/chatbot/feedback', null, { params });
      
      logger.debug('✅ 피드백 제출 완료');
      return true;
    } catch (error) {
      logger.error('❌ 피드백 제출 실패:', error);
      return false;
    }
  }

  /**
   * 🔥 메시지 히스토리 저장
   */
  async saveMessageHistory() {
    try {
      const historyKey = this._getHistoryKey(
        this.currentUserId, 
        this.currentDeviceId
      );
      localStorage.setItem(historyKey, JSON.stringify(this.messageHistory));
      logger.debug(`💾 메시지 히스토리 저장 완료: ${this.messageHistory.length}개`);
    } catch (error) {
      logger.error('❌ 메시지 히스토리 저장 실패:', error);
    }
  }

  /**
   * 🔥 메시지 히스토리 로드
   */
  async loadMessageHistory() {
    try {
      const historyKey = this._getHistoryKey(
        this.currentUserId, 
        this.currentDeviceId
      );
      const history = localStorage.getItem(historyKey);
      
      if (history) {
        const parsedHistory = JSON.parse(history);

        this.messageHistory = parsedHistory.map((msg, index) => {
          if (msg.id && msg.type) {
            return msg;
          }

          return {
            id: msg.id || `msg-migrated-${Date.now()}-${index}`,
            type: msg.role === 'user' ? 'user' : 'bot',
            role: msg.role || (msg.type === 'user' ? 'user' : 'assistant'),
            content: msg.content,
            timestamp: msg.timestamp,
            suggestions: msg.suggestions,
            related_companies: msg.related_companies,
            action: msg.action,
            action_payload: msg.action_payload
          };
        });

        logger.debug(`✅ 메시지 히스토리 로드 완료: ${this.messageHistory.length}개`);
      } else {
        // 🤖 Phase 3B: localStorage 비어있으면 서버에서 복원 시도
        if (this.conversationId) {
          try {
            logger.debug('📡 서버에서 대화 기록 복원 시도...');
            const serverMessages = await this.getConversationHistory();
            if (serverMessages && serverMessages.length > 0) {
              this.messageHistory = serverMessages.map((msg, index) => ({
                id: msg.id || `msg-server-${Date.now()}-${index}`,
                type: msg.role === 'user' ? 'user' : 'bot',
                role: msg.role || 'assistant',
                content: msg.content,
                timestamp: msg.timestamp || msg.created_at,
              }));
              logger.debug(`✅ 서버에서 ${this.messageHistory.length}개 메시지 복원 완료`);
            } else {
              this.messageHistory = [];
            }
          } catch (serverError) {
            logger.warn('⚠️ 서버 히스토리 복원 실패:', serverError);
            this.messageHistory = [];
          }
        } else {
          this.messageHistory = [];
        }
      }
    } catch (error) {
      logger.error('❌ 메시지 히스토리 로드 실패:', error);
      this.messageHistory = [];
    }
  }

  /**
   * 로컬 메시지 히스토리 가져오기
   */
  getLocalMessageHistory() {
    return this.messageHistory.map((msg, index) => {
      if (!msg.id || !msg.type || !msg.role) {
        logger.warn(`⚠️ 메시지 형식 오류 (index ${index}):`, msg);
        
        return {
          id: msg.id || `msg-fixed-${Date.now()}-${index}`,
          type: msg.type || (msg.role === 'user' ? 'user' : 'bot'),
          role: msg.role || (msg.type === 'user' ? 'user' : 'assistant'),
          content: msg.content || '',
          timestamp: msg.timestamp || new Date().toISOString(),
          suggestions: msg.suggestions,
          related_companies: msg.related_companies,
          action: msg.action,
          action_payload: msg.action_payload
        };
      }
      
      return msg;
    });
  }

  /**
   * 수동 재초기화
   */
  async reinitialize() {
    logger.debug('🔄 챗봇 서비스 재초기화 시작...');
    this.isInitialized = false;
    await this.initialize();
  }

  /**
   * 서비스 상태 확인
   */
  async getServiceStatus() {
    try {
      const response = await apiClient.get('/chatbot/health');
      return {
        isHealthy: response.status === 'healthy',
        ...response
      };
    } catch (error) {
      logger.error('❌ 서비스 상태 확인 실패:', error);
      return {
        isHealthy: false,
        error: error.message
      };
    }
  }

  /**
   * 지원 언어 목록 조회
   */
  async getSupportedLanguages() {
    try {
      const response = await apiClient.get('/chatbot/supported-languages');
      return response.languages || [];
    } catch (error) {
      logger.error('❌ 지원 언어 조회 실패:', error);
      return [
        { code: 'ko', name: '한국어' },
        { code: 'en', name: 'English' },
        { code: 'ja', name: '日本語' },
        { code: 'zh', name: '中文' }
      ];
    }
  }

  /**
   * 컨텍스트 정보 조회 (디버깅용)
   */
  getContext() {
    return {
      deviceId: this.currentDeviceId,
      userId: this.currentUserId,
      conversationId: this.conversationId,
      isInitialized: this.isInitialized,
      messageCount: this.messageHistory.length
    };
  }
}

// 싱글톤 인스턴스 생성
const chatbotService = new ChatbotService();

// named export 추가
export { chatbotService };

// default export 유지
export default chatbotService;